import React, { useContext, useEffect, useState } from "react";
import { ModalContext } from "../../context/ModalContext";
import { LocationsContext } from "../../context/LocationsContext";
import SearchSelect from "../common/SearchSelect";
import ApplicationsService from "../../services/ApplicationsService";
import "./PartnerApplyForm.css";

const ROLE_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "manager", label: "Manager" },
  { value: "head_coach", label: "Head Coach" },
  { value: "otro", label: "Otro" },
];

const PartnerApplyForm = ({ handleCancel }) => {
  const { clearModal } = useContext(ModalContext);
  const { countries, getCountries } = useContext(LocationsContext);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1
    full_name: "",
    role: "",
    email: "",
    phone: "",
    country_code: null,
    // Step 2
    studio_name: "",
    legal_name: "",
    city: "",
    neighborhood: "",
    address: "",
    maps_link: "",
    year_opened: "",
    website: "",
    instagram: "",
    booking_link: "",
    // Step 3
    has_certificate: null,
    certification_name: "",
    certificate_date: "",
  });

  useEffect(() => {
    getCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const progressPercent = (step / 3) * 100;

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handlePrev = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    ApplicationsService.postApplication(formData)
      .then(() => {
        clearModal();
      })
      .catch((err) => {
        console.error(err);
        alert("Error al enviar la solicitud. Por favor intenta de nuevo.");
      });
  };

  const renderStep1 = () => (
    <div className="partner-form__step">
      <h6 className="partner-form__step-title">1) Datos de contacto (Principal)</h6>
      <div className="row g-2">
        <div className="col-12">
          <label className="form-label">Nombre completo *</label>
          <input
            type="text"
            className="form-control"
            value={formData.full_name}
            onChange={(e) => setField("full_name", e.target.value)}
            required
          />
        </div>
        <div className="col-12">
          <label className="form-label">Cargo/rol *</label>
          <select
            className="form-select"
            value={formData.role}
            onChange={(e) => setField("role", e.target.value)}
            required
          >
            <option value="">Seleccionar...</option>
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12">
          <label className="form-label">Email *</label>
          <input
            type="email"
            className="form-control"
            value={formData.email}
            onChange={(e) => setField("email", e.target.value)}
            required
          />
        </div>
        <div className="col-12">
          <label className="form-label">Teléfono / WhatsApp *</label>
          <input
            type="tel"
            className="form-control"
            value={formData.phone}
            onChange={(e) => setField("phone", e.target.value)}
            required
          />
        </div>
        <div className="col-12">
          <label className="form-label">País *</label>
          {Array.isArray(countries) ? (
            <SearchSelect
              itemText="name"
              itemValue="iso2"
              items={countries}
              value={formData.country_code}
              modifier={(opt) => setField("country_code", opt?.value ?? null)}
              placeholderText="Seleccionar país"
            />
          ) : (
            <div className="spinner-border spinner-border-sm" />
          )}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="partner-form__step">
      <h6 className="partner-form__step-title">2) Información del estudio</h6>
      <div className="row g-2">
        <div className="col-12">
          <label className="form-label">Nombre comercial del estudio *</label>
          <input
            type="text"
            className="form-control"
            value={formData.studio_name}
            onChange={(e) => setField("studio_name", e.target.value)}
            required
          />
        </div>
        <div className="col-12">
          <label className="form-label">Razón social (si aplica)</label>
          <input
            type="text"
            className="form-control"
            value={formData.legal_name}
            onChange={(e) => setField("legal_name", e.target.value)}
          />
        </div>
        <div className="col-12">
          <label className="form-label">Ciudad *</label>
          <input
            type="text"
            className="form-control"
            value={formData.city}
            onChange={(e) => setField("city", e.target.value)}
            required
          />
        </div>
        <div className="col-12">
          <label className="form-label">Colonia/Zona</label>
          <input
            type="text"
            className="form-control"
            value={formData.neighborhood}
            onChange={(e) => setField("neighborhood", e.target.value)}
          />
        </div>
        <div className="col-12">
          <label className="form-label">Dirección completa *</label>
          <input
            type="text"
            className="form-control"
            value={formData.address}
            onChange={(e) => setField("address", e.target.value)}
            required
          />
        </div>
        <div className="col-12">
          <label className="form-label">Link de Google Maps *</label>
          <input
            type="url"
            className="form-control"
            value={formData.maps_link}
            onChange={(e) => setField("maps_link", e.target.value)}
            placeholder="https://..."
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label">Año de apertura</label>
          <input
            type="text"
            className="form-control"
            value={formData.year_opened}
            onChange={(e) => setField("year_opened", e.target.value)}
            placeholder="Ej: 2024"
          />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label">Sitio web (si tienen)</label>
          <input
            type="url"
            className="form-control"
            value={formData.website}
            onChange={(e) => setField("website", e.target.value)}
          />
        </div>
        <div className="col-12">
          <label className="form-label">Instagram del estudio *</label>
          <input
            type="text"
            className="form-control"
            value={formData.instagram}
            onChange={(e) => setField("instagram", e.target.value)}
            placeholder="@estudio"
            required
          />
        </div>
        <div className="col-12">
          <label className="form-label">Link de reservas / booking (si tienen)</label>
          <input
            type="url"
            className="form-control"
            value={formData.booking_link}
            onChange={(e) => setField("booking_link", e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="partner-form__step">
      <h6 className="partner-form__step-title">3) TBM Certifications + TBM Certificate</h6>
      <div className="row g-2">
        <div className="col-12">
          <label className="form-label">¿Cuentas con TBM Certificate? *</label>
          <div className="d-flex gap-3">
            <label className="form-check">
              <input
                type="radio"
                name="has_certificate"
                checked={formData.has_certificate === true}
                onChange={() => setField("has_certificate", true)}
              />
              <span className="ms-2">Sí</span>
            </label>
            <label className="form-check">
              <input
                type="radio"
                name="has_certificate"
                checked={formData.has_certificate === false}
                onChange={() => setField("has_certificate", false)}
              />
              <span className="ms-2">No</span>
            </label>
          </div>
        </div>
        {formData.has_certificate === false && (
          <div className="col-12">
            <p className="partner-form__no-cert-msg text-danger">
              Para aplicar, es necesario contar con TBM Certificate.
            </p>
          </div>
        )}
        {formData.has_certificate === true && (
          <>
            <div className="col-12">
              <label className="form-label">Nombre de tu certificación TBM (tal como aparece en tu certificado) *</label>
              <input
                type="text"
                className="form-control"
                value={formData.certification_name}
                onChange={(e) => setField("certification_name", e.target.value)}
                required
              />
            </div>
            <div className="col-12">
              <label className="form-label">Fecha en la que obtuviste tu TBM Certificate *</label>
              <input
                type="date"
                className="form-control"
                value={formData.certificate_date}
                onChange={(e) => setField("certificate_date", e.target.value)}
                required
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  const canProceed = () => {
    if (step === 1) {
      return formData.full_name && formData.role && formData.email && formData.phone && formData.country_code;
    }
    if (step === 2) {
      return formData.studio_name && formData.city && formData.address && formData.maps_link && formData.instagram;
    }
    if (step === 3) {
      if (formData.has_certificate === false) return false;
      return formData.has_certificate === true && formData.certification_name && formData.certificate_date;
    }
    return false;
  };

  return (
    <form onSubmit={handleSubmit} className="partner-form">
      <div className="partner-form__progress mb-4">
        <div className="partner-form__progress-bar" style={{ width: `${progressPercent}%` }} />
        <div className="partner-form__progress-steps">
          <span className={step >= 1 ? "active" : ""}>1</span>
          <span className={step >= 2 ? "active" : ""}>2</span>
          <span className={step >= 3 ? "active" : ""}>3</span>
        </div>
      </div>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}

      <div className="partner-form__actions d-flex justify-content-between mt-4">
        <button type="button" className="btn btn-outline-secondary" onClick={step === 1 ? handleCancel : handlePrev}>
          {step === 1 ? "Cancelar" : "Anterior"}
        </button>
        {step < 3 ? (
          <button type="button" className="btn btn-primary monterrey-degraded-btn text-white" onClick={handleNext} disabled={!canProceed()}>
            Siguiente
          </button>
        ) : (
          <button type="submit" className="btn btn-primary monterrey-degraded-btn text-white" disabled={!canProceed()}>
            Enviar solicitud
          </button>
        )}
      </div>
    </form>
  );
};

export default PartnerApplyForm;
